/**
 * The meeting about a year's objectives when it is the year-end check-in of the year before
 * (HR UAT list #27): next year's objectives are agreed in that check-in, so no separate
 * meeting is booked. The session follows the check-in on the server
 * (performance-service app/services/year_end_objective_setting.py).
 */
import { CalendarCheck, Clock } from 'lucide-react'
import { when } from './yearEndMeeting'

interface YearEndMeetingBannerProps {
  session: { status: string; scheduled_date?: string | null }
  /** The year the objectives are for; the check-in is the year before's. */
  year: number
  memberName: string
  /** Whose screen: the People Leader approves, the Team Member is told what happens. */
  audience: 'manager' | 'employee'
}

export default function YearEndMeetingBanner({ session, year, memberName, audience }: YearEndMeetingBannerProps) {
  const checkin = `${year - 1} year-end check-in`
  const whose = audience === 'manager' ? `${memberName}'s` : 'your'
  const held = session.status === 'completed'
  const booked = session.status === 'scheduled' && session.scheduled_date

  let detail: string
  if (held) {
    detail = audience === 'manager'
      ? `Agreed in ${whose} ${checkin}. You can approve these objectives now.`
      : `Agreed in ${whose} ${checkin}. Your People Leader approves them from here.`
  } else if (booked) {
    detail = `Agreed in ${whose} ${checkin} on ${when(session.scheduled_date!)}. ` +
      (audience === 'manager' ? 'Approvals open when the check-in starts.' : 'Your People Leader approves them then.')
  } else {
    detail = `Agreed in ${whose} ${checkin}, once it is booked. No separate meeting is needed.` +
      (audience === 'manager' ? ' Book it on the People Leader Hub.' : '')
  }

  return (
    <div
      role="status"
      className={`rounded-lg p-3 sm:p-4 border ${held ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${held ? 'bg-emerald-100' : 'bg-blue-100'}`}>
          {held
            ? <CalendarCheck className="h-5 w-5 text-emerald-600" />
            : <Clock className="h-5 w-5 text-blue-600" />}
        </div>
        <div className="flex-1">
          <h4 className={`font-semibold ${held ? 'text-emerald-900' : 'text-blue-900'}`}>
            {year} objectives: part of the {checkin}
          </h4>
          <p className={`text-sm mt-1 ${held ? 'text-emerald-700' : 'text-blue-700'}`}>{detail}</p>
        </div>
      </div>
    </div>
  )
}
